import classNames from 'classnames';
import type { ComponentPropsWithoutRef } from 'react';

import { Button, ButtonGroup, RevelioTooltip } from '@revelio/ui';

import styles from './SelectionListControls.module.css';

const DEFAULT_SUBMIT_DISABLED_TEXT =
  'Please make the minimum required selections';

type SelectionListControlsProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'onSubmit'
> & {
  onClear?: () => void;
  onSubmit: () => void;
  onClose?: () => void;
  clearText?: string;
  cancelText?: string;
  submitText?: string;
  isSubmitDisabled?: boolean;
  className?: string;
  /** Why submit is disabled. Defaults to the minimum-selections reason. */
  tooltipText?: string;
};

export const SelectionListControls = ({
  onClear,
  onSubmit,
  onClose,
  clearText = 'Clear selections',
  cancelText = 'Cancel',
  submitText = 'Add',
  className,
  isSubmitDisabled,
  tooltipText,
  ...rest
}: SelectionListControlsProps) => {
  // `RevelioTooltip` listens for pointer events on a wrapper span, and a
  // natively disabled button swallows them rather than letting them reach it,
  // so `title` is the surface that reliably carries the reason. Set it only
  // while disabled — an enabled button should not explain why it is not.
  const submitDisabledText = isSubmitDisabled
    ? (tooltipText ?? DEFAULT_SUBMIT_DISABLED_TEXT)
    : undefined;

  return (
    <div
      className={classNames(styles.selectionListControls, className)}
      {...rest}
    >
      {onClear && (
        <Button
          className={styles.clearButton}
          variant="ghost"
          color="destructive"
          onClick={onClear}
        >
          {clearText}
        </Button>
      )}
      <ButtonGroup className={styles.buttonGroup}>
        <Button
          className={styles.cancelButton}
          size="xs"
          variant="link"
          onClick={onClose}
        >
          {cancelText}
        </Button>
        <RevelioTooltip
          isDisabled={!isSubmitDisabled}
          label={submitDisabledText ?? DEFAULT_SUBMIT_DISABLED_TEXT}
        >
          <Button
            className={styles.submitButton}
            data-testid="filter-popover-submit"
            color="primary"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            title={submitDisabledText}
          >
            {submitText}
          </Button>
        </RevelioTooltip>
      </ButtonGroup>
    </div>
  );
};
